import { ContentState } from "draft-js";
import { I18n } from "@assets/strings";
import { List, Set } from "immutable";
import { difference } from "lodash";
import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { v4 as uuid } from "uuid";
import { useServices } from "@core/servicesContext";
import { Track, TrackLevel } from "@domain/organization/track/track";
import { withId } from "@utils/domain";
import { useAsync } from "@utils/hooks";
import { LoadingButton } from "@ui/components/buttons/loadingButton";
import { FormRichInput } from "@ui/components/inputs/draft/formRichInput";
import { useForm } from "@ui/components/inputs/hooks";
import { SimpleTextInput } from "@ui/components/inputs/simpleTextInput";
import { stack } from "@ui/components/layout";
import { EditableProfileImage } from "@ui/components/profile/editableProfileImage";
import { ClosableModal } from "@ui/modals/closableModal";
import { gap } from "@ui/styles/utils";
import { createToast } from "@ui/toasts/utils";
import { TrackLevelsEditor } from "./trackLevelsEditor";

interface TrackEditorModalProps extends React.HTMLAttributes<HTMLDivElement> {
	organizationId: string;
	initialTrack?: Track;
	close: () => void;
}
export const TrackEditorModal: React.FC<TrackEditorModalProps> = ({
	close,
	initialTrack,
	organizationId,
	...htmlProps
}) => {
	const { organizationService } = useServices();
	const trackId = useMemo(() => initialTrack?.id ?? uuid(), [initialTrack]);
	const { fieldProps, formValue } = useForm({ name: initialTrack?.name ?? "" }, ["name"], {
		name: (n) => n.length < 128,
	});

	const [description, setDescription] = useState(initialTrack?.description ?? ContentState.createFromText(""));
	const [levels, setLevels] = useState<Set<string>>(Set(initialTrack?.levels.map((lv) => lv.title) ?? []));
	const [trackBanner, setTrackBanner] = useState(initialTrack?.banner);

	const trackDraft = {
		id: trackId,
		name: formValue.name,
		description,
	};

	const { loading, run } = useAsync(
		(track: Track) => organizationService.saveTrack(organizationId, track, initialTrack?.id),
		[organizationId, initialTrack]
	);

	return (
		<Container
			close={close}
			title={initialTrack ? I18n.t("organization.tracks.edit") : I18n.t("organization.tracks.new")}
			keyboardEventDisabled
			{...htmlProps}
		>
			<form
				onKeyDown={(e) => {
					if (e.key === "Enter") {
						e.preventDefault();
					}
				}}
				onSubmit={async (e) => {
					e.preventDefault();
					const trackLevels = (initialTrack?.levels ?? List<TrackLevel>()).filter((level) => levels.has(level.title));
					const newLevels = difference(levels.toArray(), trackLevels.map((lv) => lv.title).toArray()).map((newLevel) =>
						withId({ title: newLevel, diplomas: List() })
					);

					const track = { ...trackDraft, levels: trackLevels.concat<TrackLevel>(newLevels), bannerId: trackBanner?.id };
					await run(track);
					createToast(I18n.t("success"), I18n.t("organization.track.save.success"), "success");
					close();
				}}
			>
				<SimpleTextInput {...fieldProps("name")} placeholder={I18n.t("organization.track.name.placeholder")} />
				<FormRichInput
					value={description}
					onChange={(value) => setDescription(value)}
					placeholder={I18n.t("organization.track.description.placeholder")}
				/>
				{/* add banner track */}
				<EditableProfileImage
					image={trackBanner}
					label={I18n.t(initialTrack?.banner ? "organization.banner.edit" : "organization.banner.add")}
					type="banner"
					onUpload={(media) => {
						setTrackBanner(media);
					}}
					onDelete={() => {
						setTrackBanner(undefined);
					}}
				/>

				<TrackLevelsEditor levels={levels} setLevels={setLevels} />
				<LoadingButton loading={loading} type="submit" disabled={!formValue.name}>
					{I18n.t(initialTrack ? "validate" : "create")}
				</LoadingButton>
			</form>
		</Container>
	);
};

const Container = styled(ClosableModal)`
	${gap(40)};
	> form {
		${stack(20, "flex-start", "stretch")};
		> button {
			margin: 0 auto;
		}
	}
`;
